// Programmatic Google Places search — lets us search by BUSINESS NAME (not
// just address) from inside our own dropdown UI, then fetch full details
// (address + coordinates + Place ID) for the chosen result.
//
// This powers the location picker's "From Google" fallback: type
// "Diamonds Direct" and get the real place with its address and a
// canonical Place ID, so the new location is dedup-proof from day one.
//
// Uses the AutocompleteService (predictions) + PlacesService (details)
// from the Maps JS API already loaded by PlacesAutocomplete.

import { loadMapsScript } from "../components/PlacesAutocomplete";

let acService = null;
let placesService = null;

async function ensureServices() {
  await loadMapsScript();
  const g = window.google;
  if (!g?.maps?.places) throw new Error("Google Places not available");
  if (!acService) acService = new g.maps.places.AutocompleteService();
  if (!placesService) {
    // PlacesService needs a DOM node (or map) to attach to; a detached
    // div is fine for details lookups.
    placesService = new g.maps.places.PlacesService(document.createElement("div"));
  }
  return { g, acService, placesService };
}

/**
 * Autocomplete predictions for a free-text query (business names OR
 * addresses). Returns [] on any error or empty/short input.
 *
 * @param {string} input
 * @returns {Promise<Array<{ placeId, primary, secondary, description }>>}
 */
export async function getPlacePredictions(input) {
  const q = (input || "").trim();
  if (q.length < 3) return [];
  try {
    const { g, acService } = await ensureServices();
    return await new Promise((resolve) => {
      acService.getPlacePredictions({ input: q }, (preds, status) => {
        if (status !== g.maps.places.PlacesServiceStatus.OK || !preds) {
          resolve([]);
          return;
        }
        resolve(
          preds.map((p) => ({
            placeId: p.place_id,
            primary: p.structured_formatting?.main_text || p.description,
            secondary: p.structured_formatting?.secondary_text || "",
            description: p.description,
          }))
        );
      });
    });
  } catch {
    return [];
  }
}

/**
 * Full details for a Place ID: name, formatted address, coordinates, id.
 * Returns null on error.
 *
 * @param {string} placeId
 * @returns {Promise<{ name, address, lat, lng, placeId }|null>}
 */
export async function getPlaceDetails(placeId) {
  if (!placeId) return null;
  try {
    const { g, placesService } = await ensureServices();
    return await new Promise((resolve) => {
      placesService.getDetails(
        { placeId, fields: ["name", "formatted_address", "geometry", "place_id"] },
        (place, status) => {
          if (status !== g.maps.places.PlacesServiceStatus.OK || !place) {
            resolve(null);
            return;
          }
          resolve({
            name: place.name || "",
            address: place.formatted_address || "",
            lat: place.geometry?.location?.lat() ?? null,
            lng: place.geometry?.location?.lng() ?? null,
            placeId: place.place_id || placeId,
          });
        }
      );
    });
  } catch {
    return null;
  }
}
